import {
  getV1CompaniesCompanyIdMaterialTypes,
  patchV1CompaniesCompanyIdMaterialTypesId,
  postV1CompaniesCompanyIdMaterialTypes,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { MaterialType } from '~hooks/useMaterials/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { EntityCSVConfig } from './types';

const materialTypesColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Aggregate',
    description: 'Material type name (must be unique)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'MTT-001',
    description: 'Your internal reference ID',
  },
];

// Material type create/update endpoints are company-scoped (/v1/companies/{company-id}/material_types).
// The shared EntityCSVConfig.update signature only carries the entity id, so the config is built per
// company and closes over the scoped company id for the update path.
export const createMaterialTypesConfig = (
  scopedCompanyId: string,
): EntityCSVConfig<MaterialType> => ({
  entityType: 'material_types',
  columns: materialTypesColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdMaterialTypes({
          path: { 'company-id': companyId },
          query,
        }),
      MaterialType.parse,
    ),

  create: async (
    companyId: string,
    data: Record<string, string>,
  ): Promise<MaterialType> => {
    const response = await postV1CompaniesCompanyIdMaterialTypes({
      path: { 'company-id': companyId },
      body: {
        name: data.name,
        external_id: data.external_id || undefined,
      },
    });

    return MaterialType.parse(response.data.data);
  },

  update: async (id: string, data: Record<string, string>): Promise<MaterialType> => {
    const response = await patchV1CompaniesCompanyIdMaterialTypesId({
      path: { 'company-id': scopedCompanyId, id },
      body: {
        name: data.name,
        external_id: data.external_id || undefined,
      },
    });

    return MaterialType.parse(response.data.data);
  },

  transformExport: (materialType: MaterialType): Record<string, string> => ({
    id: materialType.id,
    name: materialType.name,
    external_id: materialType.externalId || '',
  }),

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    external_id: row.external_id?.trim() || null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }

    return errors;
  },
});
